<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import FunnelAPI from 'dashboard/api/funnel';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';

const emit = defineEmits(['submit', 'cancel']);

// inboxesGetter: permite reusar este form no tab de SMS (passando o getter de
// inboxes shortcode) sem duplicar o formulário. Default = inboxes do WhatsApp/WAHA.
const props = defineProps({
  inboxesGetter: {
    type: String,
    default: 'inboxes/getWhatsappCampaignInboxes',
  },
});

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter(props.inboxesGetter),
  // Caixas de WhatsApp (oficial + WAHA) — alvo da escalação por palavra-chave,
  // independente do canal da campanha (que no tab de SMS é shortcode).
  whatsappInboxes: useMapGetter('inboxes/getWhatsappCampaignInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  agents: useMapGetter('agents/getAgents'),
  teams: useMapGetter('teams/getTeams'),
  // Atributos personalizados de contato (CRM) — pro seletor de audiência e p/ os
  // placeholders de texto livre.
  contactAttributes: useMapGetter('attributes/getContactAttributes'),
};

const store = useStore();
// Assistentes do Copilot (Captain) p/ a opção "Continuar com IA" — busca direta
// na API (mesmo padrão do FunnelAPI), sem acoplar ao store do Captain.
const aiAssistants = ref([]);
onMounted(async () => {
  // Garante que os agentes estejam carregados p/ os seletores de atribuição do drip
  // (a tela de campanhas não dispara agents/get por conta própria).
  store.dispatch('agents/get');
  // Atributos de contato (CRM) p/ o seletor de audiência por atributo.
  store.dispatch('attributes/get');
  try {
    const { data } = await CaptainAssistantAPI.get();
    aiAssistants.value = data?.payload || data || [];
  } catch (e) {
    aiAssistants.value = [];
  }
});

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  templateVariants: [], // [{ templateId }] — templates extras p/ A/B (oficial)
  scheduledAt: null,
  selectedAudience: [],
  audienceType: 'labels', // labels|conversation_labels|kanban|funnel|assigned_agent|conversation_status|contact_attribute|csv
  selectedFunnel: null,
  selectedStage: null,
  assignedAgentId: null,
  conversationStatus: 'open',
  contactAttrKey: '',
  contactAttrValue: '',
  // Disparo em massa v2 (drip) — envio gradual por dia
  dripEnabled: false,
  dailyLimit: 100,
  windowStart: 8,
  windowEnd: 18,
  daysOfWeek: [], // dias da semana permitidos (0=Dom..6=Sáb). Vazio = todos os dias.
  wahaMessage: '', // texto livre (canal WAHA, sem template Meta) — variante A
  messageVariants: [], // variantes B/C... do teste A/B (só texto livre)
  spintaxEnabled: false,
  minIntervalS: 2, // intervalo mínimo entre envios (segundos)
  maxIntervalS: 8, // intervalo máximo entre envios (segundos)
  // Atendimento das respostas: vendedor humano OU agente de IA (exclusivo).
  assignmentMode: 'agent', // agent | ai
  assignmentStrategy: 'none', // none | fixed | round_robin | region_ddd | team
  assignmentAgentId: null,
  assignmentAgentIds: [],
  assignmentTeamId: null,
  dddMap: [], // [{ ddd: '11', agentId: 1 }]
  regionFallbackAgentId: null,
  aiAssistantId: null, // assistente do Copilot que continua a conversa
  optOutFooter: true,
  // Fallback de nome: usado no lugar de {primeiro_nome}/{nome} quando o contato veio
  // só com telefone (sem nome real). Em branco = saudação natural sem nome.
  nameFallback: '',
  // Ativação por palavra-chave (SMS -> WhatsApp): o lead responde o SMS com uma
  // palavra positiva (SIM/QUERO/...) e a gente escala pro WhatsApp.
  kaEnabled: false,
  kaTargetInboxId: null, // caixa de WhatsApp (oficial ou WAHA) da escalação
  kaCustomKeywords: '', // palavras extras (além das padrão), separadas por vírgula
  kaMessage: '', // mensagem da escalação (texto livre p/ WAHA)
  kaTemplateId: null, // template Meta (quando o alvo é WhatsApp oficial)
  // se o lead já tem conversa aberta: fecha a antiga e recria com outro atendente
  kaResetReassign: false,
  // Mídia (imagem/PDF) do disparo — só WhatsApp WAHA. O texto vira a legenda.
  attachedMedia: null, // { blobId, fileName, fileUrl, contentType }
  mediaUploading: false,
  // Respostas do lead: copilot = fila humana + sugestão de IA (sem envio autônomo).
  requireHumanResponse: false,
  kanbanDestFunnelId: null,
  kanbanDestStageId: null,
};

// Dias da semana pro disparo gradual (cron). value = wday (0=Dom..6=Sáb).
const WEEKDAYS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];
const toggleDay = value => {
  const idx = state.daysOfWeek.indexOf(value);
  if (idx === -1) state.daysOfWeek.push(value);
  else state.daysOfWeek.splice(idx, 1);
};

// Palavras de ativação ligadas por padrão (espelha o backend — só pra exibir).
const DEFAULT_ACTIVATION_KEYWORDS = [
  'OK', 'QUERO', 'SIMULAR', 'SIM', 'SIMM', 'SIMULACAO', 'SIN', 'SIMULE',
  'MONTANTE', 'CREDITO', 'CONFIAVEL', 'QUANTO', 'VALOR', 'OI', 'GOSTARIA',
  'WHATSAPP', 'ESPERA', 'CHEGOU', 'CERTO', 'CONTINUAR', 'CONTINUA', 'TENHO',
];

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const kaTemplateParserRef = ref(null); // parser do template da escalação (alvo oficial)

// Kanban/Funnel data
const funnels = ref([]);
const funnelStages = ref([]);
const loadingFunnels = ref(false);

// CSV import data
const csvContacts = ref([]);
const csvFileName = ref('');
const csvInvalidCount = ref(0);

const audienceTypeOptions = [
  { value: 'labels', label: 'Etiquetas do contato' },
  { value: 'conversation_labels', label: 'Etiquetas da conversa' },
  { value: 'kanban', label: 'Etapa do Kanban' },
  { value: 'funnel', label: 'Funil inteiro' },
  { value: 'assigned_agent', label: 'Por vendedor atribuído' },
  { value: 'conversation_status', label: 'Status da conversa' },
  { value: 'contact_attribute', label: 'Atributo do contato (CRM)' },
  { value: 'csv', label: 'Importar CSV/Excel' },
];

const conversationStatusOptions = [
  { value: 'open', label: 'Aberta' },
  { value: 'pending', label: 'Pendente' },
  { value: 'resolved', label: 'Resolvida' },
  { value: 'snoozed', label: 'Adiada' },
];

// Fetch funnels for Kanban audience
const fetchFunnels = async () => {
  loadingFunnels.value = true;
  try {
    const { data } = await FunnelAPI.get();
    funnels.value = data || [];
  } catch (e) {
    funnels.value = [];
  } finally {
    loadingFunnels.value = false;
  }
};

const funnelOptions = computed(() =>
  funnels.value.map(f => ({ value: f.id, label: f.name }))
);

const stageOptions = computed(() => {
  if (!state.selectedFunnel) return [];
  const funnel = funnels.value.find(f => f.id === state.selectedFunnel);
  if (!funnel || !funnel.stages) return [];
  return Object.entries(funnel.stages).map(([key, stage]) => ({
    value: key,
    label: stage.name || key,
  }));
});

// Destino opcional do card no envio (response_handling=copilot).
const kanbanDestStageOptions = computed(() => {
  if (!state.kanbanDestFunnelId) return [];
  const funnel = funnels.value.find(f => f.id === state.kanbanDestFunnelId);
  if (!funnel || !funnel.stages) return [];
  return Object.entries(funnel.stages)
    .map(([key, stage]) => ({
      value: key,
      label: stage.name || key,
      position: Number(stage.position) || 0,
    }))
    .sort((a, b) => a.position - b.position);
});

// Normaliza telefone para formato WhatsApp API: apenas dígitos com DDI (ex: 5511999999999)
const normalizePhone = raw => {
  // Remove tudo que não for dígito
  let digits = (raw || '').replace(/\D/g, '');
  // Se já veio com DDI 55 sobrando, volta pra DDD+número antes de reprocessar
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  // Remove zeros à esquerda (ex: 011999... → 11999...), inclusive os reexpostos acima
  digits = digits.replace(/^0+/, '');
  // Celular antigo (DDD + 8 dígitos começando em 6-9) → insere o nono dígito
  if (digits.length === 10 && /[6-9]/.test(digits.charAt(2))) {
    digits = digits.slice(0, 2) + '9' + digits.slice(2);
  }
  // Se não tem DDI (menos de 12 dígitos para BR), adiciona 55
  if (digits.length <= 11) digits = '55' + digits;
  return digits;
};

// Handle CSV file upload
const handleCsvUpload = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  // Excel binário (.xlsx/.xls) é um ZIP — lido como texto vira lixo e dava
  // "0 contatos" sem explicação. Recusa com instrução acionável.
  if (/\.(xlsx|xls)$/i.test(file.name)) {
    useAlert(
      'Arquivo do Excel (.xlsx/.xls) não é lido aqui. Salve a planilha como CSV (UTF-8) e envie novamente.'
    );
    event.target.value = '';
    return;
  }
  csvFileName.value = file.name;
  csvInvalidCount.value = 0;

  const reader = new FileReader();
  reader.onload = e => {
    const text = e.target.result;
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    const contacts = [];
    const invalid = [];

    // Separa por , ; ou tab e tira aspas de cada célula (Excel PT-BR exporta com ; e aspas).
    const splitRow = line =>
      line.split(/[,;\t]/).map(c => c.trim().replace(/^"+|"+$/g, ''));

    // Acha a coluna do telefone (e a do nome) pelo CABEÇALHO — aceita planilhas "ricas"
    // (relatórios com várias colunas), não só o formato TELEFONE;NOME. Antes assumia a
    // 1ª coluna como telefone e dava 0 contatos quando ela era outra coisa.
    const headerCells = splitRow(lines[0] || '').map(h => h.toLowerCase());
    const findCol = keys =>
      headerCells.findIndex(h => keys.some(k => h.includes(k)));
    let phoneIdx = findCol(['telefone', 'celular', 'whatsapp', 'fone', 'phone']);
    const nameIdx = findCol(['nome', 'cliente', 'contato', 'name']);
    const hasHeader = phoneIdx !== -1 || nameIdx !== -1;
    if (phoneIdx === -1) phoneIdx = 0; // sem cabeçalho reconhecível: 1ª coluna = telefone
    const startIdx = hasHeader ? 1 : 0;

    for (let i = startIdx; i < lines.length; i++) {
      const cols = splitRow(lines[i]);
      const rawPhone = cols[phoneIdx]?.trim();
      if (!rawPhone) continue;

      let name = ((nameIdx !== -1 ? cols[nameIdx] : cols[1]) || '').trim();
      // Nome todo em CAIXA ALTA → Title Case (fica natural no {nome}). Usa separador
      // explícito (início/espaço/hífen/apóstrofo) porque \b é ASCII-only e quebraria
      // acentos (JOÃO → JoÃO).
      if (name && name === name.toUpperCase()) {
        name = name
          .toLowerCase()
          .replace(/(^|[\s\-'])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
      }

      // Só no formato simples (telefone na 1ª coluna) as colunas extras viram
      // variáveis do template ({{1}}, {{2}}...). Em planilha rica isso não se aplica.
      const params = {};
      if (phoneIdx === 0) {
        for (let c = 2; c < cols.length; c++) {
          const val = cols[c]?.trim();
          if (val) params[String(c - 1)] = val;
        }
      }

      const phone = normalizePhone(rawPhone);
      // Validação: mínimo 12 dígitos (DDI + DDD + número), máximo 15
      if (phone.length >= 12 && phone.length <= 15) {
        contacts.push({ phone, name, original: rawPhone, params });
      } else {
        invalid.push(rawPhone);
      }
    }
    csvContacts.value = contacts;
    csvInvalidCount.value = invalid.length;
    if (invalid.length > 0) {
      console.warn('[CSV] Telefones inválidos ignorados:', invalid);
    }
  };
  reader.readAsText(file);
};

// Exposto p/ o diálogo: há algo preenchido? (guarda o "clicar fora" sem perder o form)
const isDirty = () =>
  !!(
    state.title?.trim() ||
    state.inboxId ||
    state.wahaMessage?.trim() ||
    state.templateId ||
    state.scheduledAt ||
    state.attachedMedia ||
    csvContacts.value?.length ||
    state.selectedAudience?.length ||
    state.messageVariants?.length
  );
defineExpose({ isDirty });

const removeCsvFile = () => {
  csvContacts.value = [];
  csvFileName.value = '';
  csvInvalidCount.value = 0;
};

// Chip do arquivo: conta válidos E inválidos ignorados (antes os inválidos só
// apareciam no console e o operador não sabia por que "sumiram" contatos).
const csvChipText = computed(() => {
  const base = `${csvFileName.value} — ${csvContacts.value.length} válido(s)`;
  return csvInvalidCount.value > 0
    ? `${base} · ${csvInvalidCount.value} inválido(s) ignorado(s)`
    : base;
});

// Lista de descadastro (LGPD): sobe um CSV de telefones que NUNCA devem receber
// campanhas. É global por conta (vale pra todas as campanhas). Só a 1ª coluna
// (telefone) importa; o backend canonicaliza e insere em lote.
const suppressionResult = ref(null);
const handleSuppressionUpload = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = async e => {
    const lines = e.target.result.split('\n').filter(l => l.trim());
    const firstLine = lines[0]?.toLowerCase() || '';
    const startIdx =
      firstLine.includes('phone') ||
      firstLine.includes('tel') ||
      firstLine.includes('nome')
        ? 1
        : 0;
    const phones = [];
    for (let i = startIdx; i < lines.length; i += 1) {
      const raw = lines[i].split(/[,;\t]/)[0]?.trim();
      if (raw) phones.push(raw);
    }
    if (!phones.length) return;
    try {
      suppressionResult.value = await store.dispatch(
        'campaigns/suppressPhones',
        phones
      );
      useAlert(
        `Lista de descadastro: ${suppressionResult.value.suppressed} número(s) adicionado(s).`
      );
    } catch (err) {
      useAlert('Não foi possível atualizar a lista de descadastro.');
    }
  };
  reader.readAsText(file);
};

const setAudienceType = type => {
  state.audienceType = type;
};

const downloadCsvTemplate = () => {
  const content = [
    'TELEFONE;NOME',
    '+5511999999999;Maria Silva',
    '+5521988887777;João Santos',
    '+5531977776666;',
    '11966665555;Ana Costa',
  ].join('\n');
  const bom = '\uFEFF'; // BOM para Excel reconhecer UTF-8
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'modelo_campanha_whatsapp.csv';
  link.click();
  URL.revokeObjectURL(url);
};

// Texto livre (sem template Meta): WAHA (Channel::Api) e SMS shortcode (Channel::Sms).
// Declarado ANTES de `rules`/useVuelidate: o `rules` referencia isFreeTextInbox e o
// Vuelidate avalia `rules` já no setup — se isFreeTextInbox viesse depois, dava TDZ
// ("Cannot access 'U' before initialization") e o formulário INTEIRO não renderizava
// (só o título do dialog aparecia). Mantê-lo aqui em cima é o que conserta.
const selectedInboxObj = computed(() =>
  (formState.inboxes.value || []).find(i => i.id === state.inboxId)
);
const isFreeTextInbox = computed(() =>
  ['Channel::Api', 'Channel::Sms'].includes(selectedInboxObj.value?.channel_type)
);
const channelMode = computed(() => {
  const t = selectedInboxObj.value?.channel_type;
  if (t === 'Channel::Api') return 'waha';
  if (t === 'Channel::Sms') return 'sms';
  return 'official';
});

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  // WAHA não usa template Meta; e drip dispensa o agendamento manual.
  templateId: isFreeTextInbox.value ? {} : { required },
  scheduledAt: isFreeTextInbox.value || state.dripEnabled ? {} : { required },
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

// Audiência por atributo do CRM: select dos campos cadastrados (em vez de digitar a
// chave na mão). attributeKey é a chave crua (ex.: "cidade"); display é o rótulo.
const contactAttributeOptions = computed(() =>
  (formState.contactAttributes.value || []).map(a => ({
    value: a.attributeKey,
    label: a.attributeDisplayName || a.attributeKey,
  }))
);
const selectedContactAttribute = computed(() =>
  (formState.contactAttributes.value || []).find(
    a => a.attributeKey === state.contactAttrKey
  )
);
// Se o atributo escolhido for do tipo LISTA, o Valor também vira select das opções.
const contactAttrValueOptions = computed(() => {
  const attr = selectedContactAttribute.value;
  if (!attr || attr.attributeDisplayType !== 'list') return [];
  return (attr.attributeValues || []).map(v => ({ value: v, label: v }));
});
const contactAttrIsList = computed(
  () => contactAttrValueOptions.value.length > 0
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

// Disparo em massa (drip): opções de atribuição
const agentOptions = computed(() =>
  mapToOptions(formState.agents.value, 'id', 'name')
);
const teamOptions = computed(() =>
  mapToOptions(formState.teams.value, 'id', 'name')
);
// Assistentes do Copilot (Captain) p/ "Continuar com IA".
const aiAssistantOptions = computed(() =>
  mapToOptions(aiAssistants.value, 'id', 'name')
);
const assignmentModeOptions = [
  { value: 'agent', label: 'Atribuir a um vendedor' },
  { value: 'ai', label: 'Continuar com agente de IA' },
];
const assignmentStrategyOptions = [
  { value: 'none', label: 'Sem atribuição (cai no pool)' },
  { value: 'fixed', label: 'Vendedor fixo' },
  { value: 'round_robin', label: 'Rodízio na equipe' },
  { value: 'region_ddd', label: 'Por região (DDD)' },
  { value: 'team', label: 'Equipe (Team)' },
];

// region_ddd: linhas DDD -> vendedor
const addDddRow = () => state.dddMap.push({ ddd: '', agentId: null });
const removeDddRow = idx => state.dddMap.splice(idx, 1);

// Teste A/B (texto livre): variante A = mensagem principal; B/C... são extras, cada
// uma com texto + mídia opcional (no WAHA). Máx 5 no total (A + 4 extras).
const MAX_EXTRA_VARIANTS = 4;
const addVariant = () => {
  if (state.messageVariants.length < MAX_EXTRA_VARIANTS) {
    state.messageVariants.push({ text: '', media: null, uploading: false });
  }
};
const removeVariant = idx => state.messageVariants.splice(idx, 1);

// Mídia (imagem/vídeo/PDF) do disparo WAHA: sobe via /upload e guarda o blobId
// (signed_id) — o texto da variante vira a legenda no envio.
const MEDIA_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'video/mp4', 'video/3gpp', 'video/quicktime',
  'application/pdf',
];
const uploadMedia = async file => {
  if (!MEDIA_TYPES.includes(file.type)) {
    useAlert('Tipo não suportado — use imagem, vídeo (MP4) ou PDF.');
    return null;
  }
  if (file.size > 16 * 1024 * 1024) {
    useAlert('Arquivo maior que 16MB.');
    return null;
  }
  const { fileUrl, blobId } = await uploadFile(file);
  return { blobId, fileName: file.name, fileUrl, contentType: file.type };
};
// variante A (mensagem principal)
const handleMediaUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  state.mediaUploading = true;
  try {
    const media = await uploadMedia(file);
    if (media) state.attachedMedia = media;
  } catch (e) {
    useAlert('Não foi possível subir o arquivo. Tente de novo.');
  } finally {
    state.mediaUploading = false;
  }
};
const removeMedia = () => {
  state.attachedMedia = null;
};
// variantes B/C... (mídia por variante)
const handleVariantMedia = async (idx, event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  state.messageVariants[idx].uploading = true;
  try {
    const media = await uploadMedia(file);
    if (media) state.messageVariants[idx].media = media;
  } catch (e) {
    useAlert('Não foi possível subir o arquivo. Tente de novo.');
  } finally {
    state.messageVariants[idx].uploading = false;
  }
};
const removeVariantMedia = idx => {
  state.messageVariants[idx].media = null;
};

// Pré-visualização "como fica a mensagem": troca placeholders por exemplos, resolve
// spintax (1ª opção) e mostra as variantes como bolhas de WhatsApp.
const PREVIEW_SAMPLE = {
  primeiro_nome: 'Maria',
  nome: 'Maria Silva',
  email: 'maria@email.com',
  telefone: '+55 51 99999-9999',
  cidade: 'Porto Alegre',
};
const renderPreview = text => {
  let out = (text || '').toString();
  Object.entries(PREVIEW_SAMPLE).forEach(([k, v]) => {
    out = out.split(`{${k}}`).join(v);
  });
  out = out.replace(/\{\{(\d+)\}\}/g, (_, n) => `(coluna ${n})`);
  out = out.replace(/\{([^{}|]+\|[^{}]+)\}/g, (_, o) => o.split('|')[0]);
  return out;
};
const isImageMedia = m => (m?.contentType || '').startsWith('image/');
const isVideoMedia = m => (m?.contentType || '').startsWith('video/');
const previewItems = computed(() => {
  if (!isFreeTextInbox.value) return [];
  const items = [
    { label: 'A', text: renderPreview(state.wahaMessage), media: state.attachedMedia },
  ];
  (state.messageVariants || []).forEach((v, i) => {
    if ((v.text || '').trim() || v.media) {
      items.push({ label: String.fromCharCode(66 + i), text: renderPreview(v.text), media: v.media });
    }
  });
  return items;
});
const hasPreview = computed(() =>
  previewItems.value.some(i => i.text || i.media)
);

// Palavras-chave extras da ativação: aceita separar por vírgula, ; ou linha.
const parseKeywords = raw =>
  (raw || '')
    .split(/[,\n;]+/)
    .map(k => k.trim())
    .filter(Boolean);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

// ===== Múltiplos templates A/B (WhatsApp oficial) =====
// Template A = seletor principal (com parser de params); extras só selecionam o
// template (params do destinatário via CSV são mesclados no envio). Máx 5 (A + 4).
const templateById = id =>
  templateOptions.value.find(o => o.value === id)?.template;
const addTemplateVariant = () => {
  if (state.templateVariants.length < 4) {
    state.templateVariants.push({ templateId: null });
  }
};
const removeTemplateVariant = idx => state.templateVariants.splice(idx, 1);
const templateBody = tpl =>
  (tpl?.components || []).find(c => c.type === 'BODY')?.text || '';
const buildTemplateVariants = () => {
  const ids = [
    state.templateId,
    ...state.templateVariants.map(t => t.templateId),
  ].filter(Boolean);
  return [...new Set(ids)]
    .map(id => {
      const tpl = templateById(id);
      if (!tpl) return null;
      const isMain = id === state.templateId;
      return {
        name: tpl.name || '',
        namespace: tpl.namespace || '',
        category: tpl.category || 'UTILITY',
        language: tpl.language || 'en_US',
        processed_params: isMain
          ? templateParserRef.value?.processedParams || {}
          : {},
        body:
          (isMain ? templateParserRef.value?.renderedTemplate : null) ||
          templateBody(tpl),
      };
    })
    .filter(Boolean);
};

// ===== Ativação por palavra-chave: alvo da escalação (WhatsApp oficial ou WAHA) =====
const whatsappInboxOptions = computed(() =>
  mapToOptions(formState.whatsappInboxes.value, 'id', 'name')
);
const kaTargetInboxObj = computed(() =>
  (formState.whatsappInboxes.value || []).find(i => i.id === state.kaTargetInboxId)
);
// WAHA (Channel::Api) = texto livre; WhatsApp oficial = template Meta (o lead não
// tem janela de 24h aberta nesse inbox, então fora do WAHA exige template).
const kaTargetIsFreeText = computed(
  () => kaTargetInboxObj.value?.channel_type === 'Channel::Api'
);
const kaTemplateOptions = computed(() => {
  if (!state.kaTargetInboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(
    state.kaTargetInboxId
  );
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});
const kaSelectedTemplate = computed(() => {
  if (!state.kaTemplateId) return null;
  return kaTemplateOptions.value.find(o => o.value === state.kaTemplateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field]?.$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(() => {
  if (v$.value.$invalid || !hasRequiredTemplateParams.value) return true;
  if (isFreeTextInbox.value && !state.wahaMessage) return true;
  if (state.audienceType === 'labels' && (!state.selectedAudience || state.selectedAudience.length === 0)) return true;
  if (state.audienceType === 'conversation_labels' && (!state.selectedAudience || state.selectedAudience.length === 0)) return true;
  if (state.audienceType === 'kanban' && (!state.selectedFunnel || !state.selectedStage)) return true;
  if (state.audienceType === 'funnel' && !state.selectedFunnel) return true;
  if (state.audienceType === 'assigned_agent' && !state.assignedAgentId) return true;
  if (state.audienceType === 'contact_attribute' && !state.contactAttrKey) return true;
  if (state.audienceType === 'csv' && csvContacts.value.length === 0) return true;
  // Ativação por palavra-chave (SMS): se ligada, exige alvo + mensagem/template —
  // senão o backend rejeita silenciosamente e o usuário não vê o porquê.
  if (channelMode.value === 'sms' && state.kaEnabled) {
    if (!state.kaTargetInboxId) return true;
    if (kaTargetIsFreeText.value && !state.kaMessage.trim()) return true;
    if (!kaTargetIsFreeText.value && !state.kaTemplateId) return true;
  }
  return false;
});

// Diz O QUE falta pro botão Criar liberar — a PRIMEIRA pendência, espelhando as
// condições do isSubmitDisabled na mesma ordem (antes o botão só ficava cinza
// sem explicação nenhuma).
const missingRequirement = computed(() => {
  if (!state.title?.trim()) return 'Dê um nome à campanha';
  if (!state.inboxId) return 'Selecione a caixa de entrada';
  if (!isFreeTextInbox.value && !state.templateId) {
    return 'Selecione o template da mensagem';
  }
  if (!isFreeTextInbox.value && !state.dripEnabled && !state.scheduledAt) {
    return 'Defina a data de agendamento';
  }
  if (isFreeTextInbox.value && !state.wahaMessage) return 'Escreva a mensagem';
  if (
    ['labels', 'conversation_labels'].includes(state.audienceType) &&
    !state.selectedAudience?.length
  ) {
    return 'Selecione o público (etiquetas)';
  }
  if (
    state.audienceType === 'kanban' &&
    (!state.selectedFunnel || !state.selectedStage)
  ) {
    return 'Selecione o funil e a etapa do público';
  }
  if (state.audienceType === 'funnel' && !state.selectedFunnel) {
    return 'Selecione o funil do público';
  }
  if (state.audienceType === 'assigned_agent' && !state.assignedAgentId) {
    return 'Selecione o vendedor do público';
  }
  if (state.audienceType === 'contact_attribute' && !state.contactAttrKey) {
    return 'Selecione o campo do CRM do público';
  }
  if (state.audienceType === 'csv' && csvContacts.value.length === 0) {
    return 'Envie o arquivo CSV com os contatos';
  }
  if (channelMode.value === 'sms' && state.kaEnabled) {
    if (!state.kaTargetInboxId) {
      return 'Selecione a caixa de WhatsApp da ativação';
    }
    if (kaTargetIsFreeText.value && !state.kaMessage.trim()) {
      return 'Escreva a mensagem da ativação';
    }
    if (!kaTargetIsFreeText.value && !state.kaTemplateId) {
      return 'Selecione o template da ativação';
    }
  }
  return '';
});

// Fluxo drip (WAHA/SMS sempre; oficial quando o disparo em massa está ligado):
// o agendamento vira "Iniciar automaticamente em" (opcional) — espelha a condição
// do prepareCampaignDetails que anexa o drip_config.
const isDripFlow = computed(() => state.dripEnabled || isFreeTextInbox.value);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  const freeText = isFreeTextInbox.value; // WAHA ou SMS shortcode
  const details = {
    title: state.title,
    // Texto livre (WAHA/SMS, sem template Meta) ou conteúdo do template (oficial).
    message: freeText ? state.wahaMessage : templateContent,
    template_params: freeText ? {} : templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience_type: state.audienceType,
  };

  // Disparo em massa (drip): anexa drip_config. WAHA SEMPRE é drip (não tem envio
  // legado pra Channel::Api). A presença de daily_limit faz o backend tratar como
  // drip (não dispara blast; envio gradual pelo DripSchedulerJob após "Iniciar").
  if (state.dripEnabled || freeText) {
    // Atendimento das respostas: IA OU vendedor (exclusivo). Com IA, a atribuição
    // humana fica 'none' e o backend ativa o assistente do Copilot na conversa.
    const useAi = state.assignmentMode === 'ai' && state.aiAssistantId;
    const assignment = {
      strategy: useAi ? 'none' : state.assignmentStrategy,
      config: {},
    };
    if (!useAi && state.assignmentStrategy === 'fixed') {
      assignment.config.agent_id = state.assignmentAgentId;
    } else if (!useAi && state.assignmentStrategy === 'round_robin') {
      assignment.config.agent_ids = state.assignmentAgentIds;
    } else if (!useAi && state.assignmentStrategy === 'team') {
      assignment.config.team_id = state.assignmentTeamId;
    } else if (!useAi && state.assignmentStrategy === 'region_ddd') {
      assignment.config.ddd_map = state.dddMap.reduce((acc, row) => {
        if (row.ddd && row.agentId) acc[String(row.ddd)] = row.agentId;
        return acc;
      }, {});
      assignment.config.fallback_agent_id = state.regionFallbackAgentId;
    }
    details.drip_config = {
      daily_limit: Number(state.dailyLimit) || 100,
      window_start: Number(state.windowStart),
      window_end: Number(state.windowEnd),
      days_of_week: Array.isArray(state.daysOfWeek)
        ? [...state.daysOfWeek].sort((a, b) => a - b)
        : [],
      min_interval_s: Number(state.minIntervalS),
      max_interval_s: Number(state.maxIntervalS),
      assignment,
      // rodapé "responder SAIR" só faz sentido em texto livre (WAHA/SMS); no oficial
      // o texto do template é aprovado e não pode mudar.
      opt_out_footer: isFreeTextInbox.value ? state.optOutFooter : false,
      name_fallback: (state.nameFallback || '').trim(),
      channel_mode: channelMode.value,
      spintax_enabled: freeText ? state.spintaxEnabled : false,
    };

    // Agente de IA (Captain) que continua a conversa quando o lead responde.
    // Com "respostas precisam de atendente", a IA vira copiloto (suggest_auto).
    if (useAi) {
      details.drip_config.ai_agent = {
        assistant_id: state.aiAssistantId,
        mode: state.requireHumanResponse ? 'suggest_auto' : 'autonomous_suggest',
      };
    }

    // Respostas do lead: copilot = abre na fila humana; notice = comportamento atual.
    if (state.requireHumanResponse) {
      details.drip_config.response_handling = 'copilot';
      if (state.kanbanDestFunnelId && state.kanbanDestStageId) {
        details.drip_config.kanban_destination = {
          funnel_id: state.kanbanDestFunnelId,
          stage_id: state.kanbanDestStageId,
        };
      }
    }

    // Ativação por palavra-chave (SMS -> WhatsApp): o lead responde o SMS com uma
    // palavra positiva e a gente escala pro WhatsApp alvo com a msg configurada.
    if (channelMode.value === 'sms' && state.kaEnabled && state.kaTargetInboxId) {
      const ka = {
        enabled: true,
        target_inbox_id: state.kaTargetInboxId,
        keywords: parseKeywords(state.kaCustomKeywords),
        reset_and_reassign: state.kaResetReassign,
      };
      if (kaTargetIsFreeText.value) {
        ka.message = state.kaMessage;
      } else {
        const parser = kaTemplateParserRef.value;
        const tpl = kaSelectedTemplate.value;
        ka.message = parser?.renderedTemplate || '';
        ka.template_params = {
          name: tpl?.name || '',
          namespace: tpl?.namespace || '',
          category: tpl?.category || 'UTILITY',
          language: tpl?.language || 'en_US',
          processed_params: parser?.processedParams || {},
        };
      }
      details.drip_config.keyword_activation = ka;
    }

    // Teste A/B (texto livre): variante A = mensagem principal (texto + mídia no
    // WAHA); B/C... = extras (cada uma texto + mídia opcional). O backend distribui
    // pelos destinatários (variant_index) e o adapter manda a mídia de cada uma.
    if (freeText) {
      const isWaha = channelMode.value === 'waha';
      const variants = [
        { text: (state.wahaMessage || '').trim(), media_blob_id: (isWaha && state.attachedMedia?.blobId) || null },
      ];
      (state.messageVariants || []).forEach(v => {
        const txt = (v.text || '').trim();
        const media = (isWaha && v.media?.blobId) || null;
        if (txt || media) variants.push({ text: txt, media_blob_id: media });
      });
      const hasMedia = variants.some(x => x.media_blob_id);
      // só manda variantes se há mais de uma OU alguma mídia (texto único simples
      // continua usando campaign.message, sem inflar o drip_config).
      if (variants.length > 1 || hasMedia) {
        details.drip_config.message_variants = variants;
      }
    }

    // Múltiplos templates A/B (WhatsApp oficial): cada destinatário recebe um dos
    // templates em rodízio. template A = o seletor principal; extras = templateVariants.
    if (!freeText) {
      const tvs = buildTemplateVariants();
      if (tvs.length > 1) details.drip_config.template_variants = tvs;
    }
  }

  if (state.audienceType === 'labels') {
    details.audience = state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    }));
  } else if (state.audienceType === 'conversation_labels') {
    details.audience = state.selectedAudience?.map(id => ({
      id,
      type: 'ConversationLabel',
    }));
  } else if (state.audienceType === 'kanban') {
    details.audience = [{
      type: 'KanbanStage',
      funnel_id: state.selectedFunnel,
      stage_id: state.selectedStage,
    }];
  } else if (state.audienceType === 'funnel') {
    details.audience = [{ type: 'Funnel', funnel_id: state.selectedFunnel }];
  } else if (state.audienceType === 'assigned_agent') {
    details.audience = [{ type: 'AssignedAgent', agent_id: state.assignedAgentId }];
  } else if (state.audienceType === 'conversation_status') {
    details.audience = [{ type: 'ConversationStatus', status: state.conversationStatus }];
  } else if (state.audienceType === 'contact_attribute') {
    details.audience = [{
      type: 'ContactAttribute',
      key: state.contactAttrKey,
      value: state.contactAttrValue,
    }];
  } else if (state.audienceType === 'csv') {
    details.audience = csvContacts.value.map(c => ({
      type: 'PhoneNumber',
      phone_number: c.phone,
      name: c.name,
      params: c.params || {},
    }));
  }

  return details;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.templateVariants = [];
    // troca de inbox pode sair do WAHA -> limpa a mídia (só WAHA aceita).
    state.attachedMedia = null;
    (state.messageVariants || []).forEach(v => {
      if (v && typeof v === 'object') v.media = null;
    });
  }
);

// Load funnels when kanban audience type is selected
watch(
  () => state.audienceType,
  newType => {
    if ((newType === 'kanban' || newType === 'funnel') && funnels.value.length === 0) {
      fetchFunnels();
    }
    // Reset audience-specific fields
    state.selectedAudience = [];
    state.selectedFunnel = null;
    state.selectedStage = null;
    csvContacts.value = [];
    csvFileName.value = '';
    csvInvalidCount.value = 0;
  }
);

const onRequireHumanResponseChange = () => {
  if (state.requireHumanResponse && funnels.value.length === 0) {
    fetchFunnels();
  }
  if (!state.requireHumanResponse) {
    state.kanbanDestFunnelId = null;
    state.kanbanDestStageId = null;
  }
};

watch(
  () => state.kanbanDestFunnelId,
  () => {
    state.kanbanDestStageId = null;
  }
);

// Reset stage when funnel changes
watch(
  () => state.selectedFunnel,
  () => {
    state.selectedStage = null;
  }
);

// Trocar o campo de CRM limpa o valor (evita valor preso ao trocar lista<->texto).
watch(
  () => state.contactAttrKey,
  () => {
    state.contactAttrValue = '';
  }
);
</script>

<template>
  <!-- div (não <form>): este componente vive dentro do <form> do DS Dialog; aninhar
       forms é HTML inválido. O submit é pelo botão "Criar" (@click), não por Enter. -->
  <div class="flex flex-col gap-4">
    <p class="text-xs text-n-slate-11 p-2.5 rounded-xl bg-n-alpha-2 border border-n-weak">
      📣 Um disparo manda a mesma mensagem pra vários contatos de uma vez. Preencha de
      cima pra baixo: <strong>por onde</strong> sai (caixa), <strong>o que</strong>
      enviar (mensagem) e <strong>pra quem</strong> (público). As opções mudam conforme
      o canal. O avançado (ritmo, atendimento, LGPD) fica recolhido lá embaixo.
    </p>
    <!-- 1º passo: escolher a caixa de entrada. O CANAL dela define as opções
         abaixo (oficial Meta = template aprovado; WAHA/SMS = texto livre). -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="text-xs text-n-slate-10">
        É por onde a campanha sai (o número/canal do envio). O canal escolhido define
        as opções abaixo: WhatsApp oficial usa template aprovado; WhatsApp não oficial
        (WAHA) e SMS usam texto livre. Escolha esta primeiro.
      </p>
    </div>

    <div class="flex flex-col gap-1">
      <Input
        v-model="state.title"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        :message="formErrors.title"
        :message-type="formErrors.title ? 'error' : 'info'"
      />
      <p class="text-xs text-n-slate-10">
        Nome interno só pra você achar a campanha depois nos relatórios — o cliente
        não vê. Ex.: "Promo Black Friday — leads do Kanban".
      </p>
    </div>

    <!-- Enquanto não há caixa de entrada, as opções de mensagem (que dependem do
         canal) ficam ocultas — escolha a inbox primeiro. -->
    <p v-if="!state.inboxId" class="text-xs text-n-slate-10">
      Escolha a caixa de entrada acima para configurar a mensagem da campanha.
    </p>

    <!-- Oficial (Meta Cloud): seleciona template aprovado -->
    <div v-if="state.inboxId && !isFreeTextInbox" class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser (oficial) -->
    <WhatsAppTemplateParser
      v-if="state.inboxId && !isFreeTextInbox && selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      hide-media-input
    />

    <!-- Múltiplos templates A/B (oficial): templates extras, alternam nos envios -->
    <div
      v-if="state.inboxId && !isFreeTextInbox && state.templateId"
      class="flex flex-col gap-2"
    >
      <div
        v-for="(tv, idx) in state.templateVariants"
        :key="idx"
        class="flex flex-col gap-1 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
      >
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            Template {{ String.fromCharCode(66 + idx) }} (A/B)
          </label>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-red-10 text-xs"
            @click="removeTemplateVariant(idx)"
          >
            ✕ remover
          </button>
        </div>
        <ComboBox
          v-model="tv.templateId"
          :options="templateOptions"
          placeholder="Selecionar template"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>
      <button
        v-if="state.templateVariants.length < 4"
        type="button"
        class="self-start text-xs font-medium text-n-blue-11 hover:underline"
        @click="addTemplateVariant"
      >
        + Adicionar template (A/B)
      </button>
      <p v-if="state.templateVariants.length" class="text-xs text-n-slate-10">
        Os templates são distribuídos alternadamente entre os contatos (teste A/B).
        Os extras usam os parâmetros padrão do template + colunas do CSV.
      </p>
    </div>

    <!-- Texto livre (WAHA / SMS shortcode): mensagem + spintax -->
    <div v-if="state.inboxId && isFreeTextInbox" class="flex flex-col gap-2">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">Mensagem</label>
        <textarea
          v-model="state.wahaMessage"
          rows="4"
          class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm"
          placeholder="Olá {nome}! ... (use {nome} e {{1}}, {{2}} das colunas do CSV)"
        />
        <p class="text-xs text-n-slate-10">
          Texto livre. Placeholders:
          <span class="font-mono">{primeiro_nome}</span>,
          <span class="font-mono">{nome}</span>,
          <span class="font-mono">{email}</span>,
          <span class="font-mono">{telefone}</span>,
          atributos do CRM (ex.: <span class="font-mono">{cidade}</span>) e
          <span class="font-mono">{{1}}</span>/<span class="font-mono">{{2}}</span>
          (colunas do CSV).
        </p>
      </div>
      <!-- Mídia da variante A (imagem/vídeo/PDF) — só WAHA. O texto acima = legenda. -->
      <div v-if="channelMode === 'waha'" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          Mídia da variante A (imagem, vídeo ou PDF) — opcional
        </label>
        <div v-if="!state.attachedMedia">
          <label
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak self-start"
          >
            <span v-if="state.mediaUploading" class="i-ri-loader-4-line animate-spin" />
            <span v-else class="i-ri-attachment-line" />
            {{ state.mediaUploading ? 'Enviando…' : 'Anexar arquivo' }}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/3gpp,video/quicktime,application/pdf"
              class="hidden"
              :disabled="state.mediaUploading"
              @change="handleMediaUpload"
            />
          </label>
        </div>
        <div
          v-else
          class="flex items-center gap-2 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
        >
          <span class="i-ri-file-line shrink-0 text-n-slate-11" />
          <span class="text-sm text-n-slate-11 flex-1 truncate">
            {{ state.attachedMedia.fileName }}
          </span>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-red-10 shrink-0"
            @click="removeMedia"
          >
            ✕
          </button>
        </div>
        <p class="text-xs text-n-slate-10">
          A mensagem acima vai como legenda. Imagem, vídeo (MP4) ou PDF até 16MB.
          Variantes B/C com mídia diferente alternam nos envios.
        </p>
      </div>

      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="state.spintaxEnabled" type="checkbox" />
        Variar o texto (spintax: {oi|olá|e aí}) — reduz risco de spam/ban
      </label>
      <p v-if="channelMode === 'waha'" class="text-xs text-n-amber-11">
        ⚠️ Envio em massa pela WAHA tem risco de ban do número. Prefira volumes baixos por dia.
      </p>

      <!-- Teste A/B: variante A = mensagem/mídia acima; B/C... abaixo (cada uma com
           texto + mídia opcional no WAHA, alternando os tipos nos envios) -->
      <div class="flex flex-col gap-2 pt-1">
        <div
          v-for="(variant, idx) in state.messageVariants"
          :key="idx"
          class="flex flex-col gap-1.5 p-2 rounded-xl bg-n-alpha-2 border border-n-weak"
        >
          <div class="flex items-center justify-between">
            <label class="text-xs font-medium text-n-slate-11">
              Variante {{ String.fromCharCode(66 + idx) }} (A/B)
            </label>
            <button
              type="button"
              class="text-n-slate-10 hover:text-n-red-10 text-xs"
              @click="removeVariant(idx)"
            >
              ✕ remover
            </button>
          </div>
          <textarea
            v-model="variant.text"
            rows="3"
            class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm"
            placeholder="Texto alternativo (mesmas variáveis {nome}/{{1}})"
          />
          <!-- mídia por variante (só WAHA): imagem, vídeo ou PDF -->
          <div v-if="channelMode === 'waha'">
            <label
              v-if="!variant.media"
              class="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak"
            >
              <span v-if="variant.uploading" class="i-ri-loader-4-line animate-spin" />
              <span v-else class="i-ri-attachment-line" />
              {{ variant.uploading ? 'Enviando…' : 'Anexar mídia (imagem/vídeo/PDF)' }}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/3gpp,video/quicktime,application/pdf"
                class="hidden"
                :disabled="variant.uploading"
                @change="e => handleVariantMedia(idx, e)"
              />
            </label>
            <div v-else class="flex items-center gap-2 text-xs">
              <span class="i-ri-file-line shrink-0 text-n-slate-11" />
              <span class="text-n-slate-11 flex-1 truncate">{{ variant.media.fileName }}</span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-red-10 shrink-0"
                @click="removeVariantMedia(idx)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
        <button
          v-if="state.messageVariants.length < 4"
          type="button"
          class="self-start text-xs font-medium text-n-blue-11 hover:underline"
          @click="addVariant"
        >
          + Adicionar variante A/B
        </button>
        <p class="text-xs text-n-slate-10">
          As variantes (texto + mídia) são distribuídas alternadamente entre os
          contatos — útil pra testar o que converte melhor. Máx. 5 (A + 4).
        </p>
      </div>

      <!-- Pré-visualização: como a mensagem fica (placeholders viram exemplos) -->
      <div v-if="hasPreview" class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">Pré-visualização</label>
        <div class="flex flex-col gap-3 p-3 rounded-xl bg-n-alpha-3 border border-n-weak">
          <div v-for="(item, i) in previewItems" :key="i" class="flex flex-col gap-0.5">
            <span
              v-if="previewItems.length > 1"
              class="text-[10px] uppercase tracking-wide text-n-slate-10"
            >
              Variante {{ item.label }}
            </span>
            <div class="self-end max-w-[85%] rounded-2xl rounded-tr-sm bg-n-teal-4 px-2.5 py-1.5">
              <img
                v-if="item.media && isImageMedia(item.media)"
                :src="item.media.fileUrl"
                class="mb-1 rounded-xl max-h-32 object-cover"
              />
              <div
                v-else-if="item.media"
                class="mb-1 flex items-center gap-1.5 rounded-xl bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >
                <span :class="isVideoMedia(item.media) ? 'i-ri-video-line' : 'i-ri-file-text-line'" />
                <span class="truncate">{{ item.media.fileName }}</span>
              </div>
              <p class="whitespace-pre-wrap break-words text-sm text-n-slate-12">
                {{ item.text || '(sem texto)' }}
              </p>
            </div>
          </div>
        </div>
        <p class="text-xs text-n-slate-10">
          Prévia aproximada — placeholders ({primeiro_nome} etc.) aparecem com exemplos.
        </p>
      </div>
    </div>

    <!-- Público: tipo de audiência + config (accordion aberto) -->
    <Accordion title="Público (quem vai receber)" :is-open="true">
      <div class="flex flex-col gap-4">
    <p class="text-xs text-n-slate-10 -mt-1">
      Quem vai receber o disparo. Escolha COMO selecionar os contatos (por etiqueta,
      etapa do Kanban, atributo do CRM, lista CSV, etc.) — só recebe quem tiver
      telefone válido e não estiver na lista de descadastro.
    </p>
    <!-- Tipo de público -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        Tipo de público
      </label>
      <div class="flex gap-1.5 flex-wrap">
        <button
          v-for="opt in audienceTypeOptions"
          :key="opt.value"
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-xl transition-colors border"
          :class="state.audienceType === opt.value
            ? 'bg-n-blue-3 border-n-blue-7 text-n-blue-11'
            : 'bg-n-alpha-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-3'"
          @click="setAudienceType(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <!-- Audiência: Etiquetas do contato -->
    <div v-if="state.audienceType === 'labels'" class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Audiência: Etiquetas da conversa -->
    <div v-if="state.audienceType === 'conversation_labels'" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        Etiquetas da conversa
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        label="Etiquetas da conversa"
        placeholder="Selecionar etiquetas de conversas"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <p class="text-xs text-n-slate-10 mt-0.5">
        Contatos de conversas com estas etiquetas receberão a campanha.
      </p>
    </div>

    <!-- Audiência: Etapa do Kanban -->
    <div v-if="state.audienceType === 'kanban'" class="flex flex-col gap-2">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">Funil</label>
        <ComboBox
          v-model="state.selectedFunnel"
          :options="funnelOptions"
          placeholder="Selecione um funil"
          :is-loading="loadingFunnels"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <div v-if="state.selectedFunnel" class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">Etapa</label>
        <ComboBox
          v-model="state.selectedStage"
          :options="stageOptions"
          placeholder="Selecione uma etapa"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        Contatos com cards nesta etapa receberão a campanha.
      </p>
    </div>

    <!-- Audiência: Funil inteiro -->
    <div v-if="state.audienceType === 'funnel'" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">Funil</label>
      <ComboBox
        v-model="state.selectedFunnel"
        :options="funnelOptions"
        placeholder="Selecione um funil"
        :is-loading="loadingFunnels"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <p class="text-xs text-n-slate-10">
        Contatos de todas as etapas deste funil receberão a campanha.
      </p>
    </div>

    <!-- Audiência: Por vendedor atribuído -->
    <div v-if="state.audienceType === 'assigned_agent'" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">Vendedor atribuído</label>
      <ComboBox
        v-model="state.assignedAgentId"
        :options="agentOptions"
        placeholder="Selecionar vendedor"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <p class="text-xs text-n-slate-10">
        Contatos de conversas atribuídas a este vendedor.
      </p>
    </div>

    <!-- Audiência: Status da conversa -->
    <div v-if="state.audienceType === 'conversation_status'" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">Status da conversa</label>
      <ComboBox
        v-model="state.conversationStatus"
        :options="conversationStatusOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Audiência: Atributo do contato (CRM) — campo via SELECT dos atributos do CRM -->
    <div v-if="state.audienceType === 'contact_attribute'" class="flex flex-col gap-2">
      <div class="flex gap-2">
        <div class="flex flex-col gap-1 flex-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">Campo do CRM</label>
          <ComboBox
            v-model="state.contactAttrKey"
            :options="contactAttributeOptions"
            placeholder="Selecionar campo"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">Valor</label>
          <ComboBox
            v-if="contactAttrIsList"
            v-model="state.contactAttrValue"
            :options="contactAttrValueOptions"
            placeholder="Selecionar valor"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <Input
            v-else
            v-model="state.contactAttrValue"
            placeholder="ex: São Paulo"
          />
        </div>
      </div>
      <p v-if="!contactAttributeOptions.length" class="text-xs text-n-amber-11">
        Nenhum atributo de contato cadastrado — crie em Configurações → Atributos
        personalizados.
      </p>
      <p v-else class="text-xs text-n-slate-10">
        Contatos cujo campo do CRM bate com o valor.
      </p>
    </div>

    <!-- Audiência: CSV/Excel -->
    <div v-if="state.audienceType === 'csv'" class="flex flex-col gap-2">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        Importar contatos (CSV/Excel)
      </label>
      <div v-if="!csvFileName" class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <label class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak">
            Selecionar arquivo
            <input
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              class="hidden"
              @change="handleCsvUpload"
            />
          </label>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3 border border-n-weak"
            @click="downloadCsvTemplate"
          >
            Baixar modelo
          </button>
        </div>
        <p class="text-xs text-n-slate-10">
          Coluna obrigatória: <strong>TELEFONE</strong> (com DDI, ex: +5511999999999). Coluna opcional: NOME.
        </p>
      </div>
      <div v-else class="flex items-center gap-2 p-2 rounded-xl bg-n-alpha-2 border border-n-weak">
        <span class="text-sm text-n-slate-11 flex-1 truncate">
          {{ csvChipText }}
        </span>
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-red-10 shrink-0"
          @click="removeCsvFile"
        >
          ✕
        </button>
      </div>
      <div v-if="csvContacts.length > 0" class="max-h-32 overflow-y-auto rounded border border-n-weak">
        <div
          v-for="(contact, idx) in csvContacts.slice(0, 10)"
          :key="idx"
          class="flex items-center gap-3 px-2 py-1 text-xs border-b border-n-weak last:border-0"
        >
          <span class="text-n-slate-11 font-mono">{{ contact.phone }}</span>
          <span v-if="contact.name" class="text-n-slate-10">{{ contact.name }}</span>
        </div>
        <div v-if="csvContacts.length > 10" class="px-2 py-1 text-xs text-n-slate-10 text-center">
          ... e mais {{ csvContacts.length - 10 }} contatos
        </div>
      </div>
    </div>

      </div>
    </Accordion>

    <!-- Opções avançadas (colapsado): disparo em massa, palavra-chave, LGPD -->
    <Accordion title="Opções avançadas (disparo gradual, ativação, LGPD)" :is-open="false">
      <div class="flex flex-col gap-4">
    <p class="text-xs text-n-slate-10 -mt-1">
      Tudo opcional — abra só se precisar. Aqui você controla o RITMO do envio (cota
      por dia + horário, pra não queimar o número), quem atende as respostas
      (vendedor ou IA), a ativação por palavra-chave no SMS e a lista de descadastro
      (LGPD).
    </p>
    <!-- Disparo em massa (drip) -->
    <div class="flex flex-col gap-3 p-3 rounded-xl bg-n-alpha-2 border border-n-weak">
      <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <input v-model="state.dripEnabled" type="checkbox" />
        Disparo em massa (envio gradual por dia)
      </label>
      <p class="text-xs text-n-slate-10 -mt-1.5">
        Em vez de enviar tudo de uma vez, envia uma cota por dia dentro da janela
        de horário, com ritmo natural — mais seguro pra grandes listas.
      </p>

      <template v-if="state.dripEnabled">
        <div class="flex gap-3">
          <Input
            v-model="state.dailyLimit"
            label="Cota diária"
            type="number"
            min="1"
            class="flex-1"
          />
          <Input
            v-model="state.windowStart"
            label="Início (h)"
            type="number"
            min="0"
            max="23"
            class="w-24"
          />
          <Input
            v-model="state.windowEnd"
            label="Fim (h)"
            type="number"
            min="1"
            max="24"
            class="w-24"
          />
        </div>

        <div class="flex gap-3 items-end">
          <Input
            v-model="state.minIntervalS"
            label="Intervalo mín. (s)"
            type="number"
            min="0"
            class="flex-1"
          />
          <Input
            v-model="state.maxIntervalS"
            label="Intervalo máx. (s)"
            type="number"
            min="0"
            class="flex-1"
          />
        </div>
        <p class="text-xs text-n-slate-10 -mt-1.5">
          Pausa aleatória entre cada envio (ritmo humano). Intervalo maior = mais
          seguro contra bloqueio, porém mais lento.
        </p>

        <!-- Dias da semana do cronograma (cron) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">Dias da semana</label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="d in WEEKDAYS"
              :key="d.value"
              type="button"
              class="px-2.5 py-1 rounded-md text-xs font-medium border transition-colors"
              :class="
                state.daysOfWeek.includes(d.value)
                  ? 'bg-n-brand/15 text-n-brand border-n-brand/40'
                  : 'bg-n-alpha-2 text-n-slate-11 border-n-weak hover:bg-n-alpha-3'
              "
              @click="toggleDay(d.value)"
            >
              {{ d.label }}
            </button>
          </div>
          <p class="text-xs text-n-slate-10">
            Nenhum marcado = todos os dias. Marque pra restringir o cronograma
            (ex.: só dias úteis). O envio só roda nos dias marcados, dentro do horário.
          </p>
        </div>

        <!-- Atendimento das respostas: vendedor humano OU agente de IA (exclusivo).
             Some com "Respostas precisam de atendente?" — copilot sempre reabre sem
             atribuição, então vendedor/equipe configurados aqui nunca seriam usados. -->
        <template v-if="!state.requireHumanResponse">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Atendimento das respostas
          </label>
          <ComboBox
            v-model="state.assignmentMode"
            :options="assignmentModeOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <!-- Agente de IA: assistente do Copilot que continua a conversa -->
        <div v-if="state.assignmentMode === 'ai'" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Agente de IA (Copilot)
          </label>
          <ComboBox
            v-model="state.aiAssistantId"
            :options="aiAssistantOptions"
            placeholder="Selecionar agente de IA"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <p class="text-xs text-n-slate-10">
            Quando o lead responder ao disparo, este agente continua a conversa
            sozinho (e pausa se um vendedor assumir). Vale nos 3 canais.
          </p>
        </div>

        <div
          v-if="state.assignmentMode === 'agent'"
          class="flex flex-col gap-1"
        >
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Atribuição automática dos contatos
          </label>
          <ComboBox
            v-model="state.assignmentStrategy"
            :options="assignmentStrategyOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div v-if="state.assignmentMode === 'agent' && state.assignmentStrategy === 'fixed'" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">Vendedor</label>
          <ComboBox
            v-model="state.assignmentAgentId"
            :options="agentOptions"
            placeholder="Selecionar vendedor"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div v-if="state.assignmentMode === 'agent' && state.assignmentStrategy === 'round_robin'" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">Vendedores no rodízio</label>
          <TagMultiSelectComboBox
            v-model="state.assignmentAgentIds"
            :options="agentOptions"
            label="Vendedores no rodízio"
            placeholder="Selecionar vendedores"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div v-if="state.assignmentMode === 'agent' && state.assignmentStrategy === 'team'" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">Equipe</label>
          <ComboBox
            v-model="state.assignmentTeamId"
            :options="teamOptions"
            placeholder="Selecionar equipe"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div v-if="state.assignmentMode === 'agent' && state.assignmentStrategy === 'region_ddd'" class="flex flex-col gap-2">
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">Vendedor padrão (sem DDD correspondente)</label>
            <ComboBox
              v-model="state.regionFallbackAgentId"
              :options="agentOptions"
              placeholder="Selecionar vendedor padrão"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
          <label class="text-sm font-medium text-n-slate-12">Mapa DDD → vendedor</label>
          <div
            v-for="(row, idx) in state.dddMap"
            :key="idx"
            class="flex gap-2 items-end"
          >
            <Input v-model="row.ddd" label="DDD" type="number" class="w-20" />
            <ComboBox
              v-model="row.agentId"
              :options="agentOptions"
              placeholder="Vendedor"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
            />
            <button
              type="button"
              class="h-9 px-2 text-n-slate-10 hover:text-n-red-10"
              @click="removeDddRow(idx)"
            >
              ✕
            </button>
          </div>
          <button
            type="button"
            class="self-start text-xs font-medium text-n-blue-11 hover:underline"
            @click="addDddRow"
          >
            + Adicionar DDD
          </button>
        </div>
        </template>

        <!-- Respostas do lead: fila humana + copiloto (sem IA autônoma) -->
        <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          <input
            v-model="state.requireHumanResponse"
            type="checkbox"
            @change="onRequireHumanResponseChange"
          />
          Respostas precisam de atendente?
        </label>
        <p class="text-xs text-n-slate-10 -mt-1.5">
          Quando o lead responder, a conversa abre na fila humana (sem atribuição
          automática). A IA só sugere o texto para o atendente aprovar — não responde sozinha.
        </p>

        <div
          v-if="state.requireHumanResponse"
          class="flex flex-col gap-2 p-2 rounded-md border border-n-weak bg-n-alpha-1"
        >
          <label class="text-sm font-medium text-n-slate-12">
            Mover card para (opcional)
          </label>
          <div class="flex gap-2">
            <ComboBox
              v-model="state.kanbanDestFunnelId"
              :options="funnelOptions"
              placeholder="Funil"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
            />
            <ComboBox
              v-model="state.kanbanDestStageId"
              :options="kanbanDestStageOptions"
              placeholder="Etapa"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
              :disabled="!state.kanbanDestFunnelId"
            />
          </div>
          <p class="text-xs text-n-slate-10">
            No momento do envio, cria ou move o card do contato para esta etapa do Kanban.
          </p>
        </div>

        <label
          v-if="isFreeTextInbox"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <input v-model="state.optOutFooter" type="checkbox" />
          Adicionar rodapé de descadastro (responder SAIR para sair)
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          <span>Saudação quando o contato não tem nome</span>
          <input
            v-model="state.nameFallback"
            type="text"
            maxlength="40"
            placeholder="Em branco = saudação natural sem nome (ex.: Oi! 👋)"
            class="px-3 py-2 text-sm border rounded-xl bg-n-alpha-2 border-n-weak text-n-slate-12"
          />
          <span class="text-xs text-n-slate-10">
            Usado no lugar de {primeiro_nome}/{nome} quando o contato veio só com
            telefone (sem nome). Ex.: "cliente" → "Oi, cliente!". Em branco, a
            saudação fica natural sem o nome (ex.: "Oi! 👋").
          </span>
        </label>
      </template>
    </div>

    <!-- Ativação por palavra-chave (SMS -> WhatsApp): só para campanhas de SMS -->
    <div
      v-if="channelMode === 'sms'"
      class="flex flex-col gap-3 p-3 rounded-xl bg-n-alpha-2 border border-n-weak"
    >
      <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <input v-model="state.kaEnabled" type="checkbox" />
        Ativação por palavra-chave (continuar no WhatsApp)
      </label>
      <p class="text-xs text-n-slate-10 -mt-1.5">
        Quando o lead responder o SMS com uma palavra positiva (ex.: SIM, QUERO),
        a gente abre a conversa numa caixa de WhatsApp e manda a mensagem
        configurada aqui.
      </p>

      <template v-if="state.kaEnabled">
        <!-- caixa de WhatsApp alvo (oficial ou WAHA) -->
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Caixa de WhatsApp da ativação
          </label>
          <ComboBox
            v-model="state.kaTargetInboxId"
            :options="whatsappInboxOptions"
            placeholder="Selecionar caixa de WhatsApp"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <p v-if="!whatsappInboxOptions.length" class="text-xs text-n-amber-11">
            Nenhuma caixa de WhatsApp encontrada — conecte uma (oficial ou WAHA)
            para usar a ativação.
          </p>
        </div>

        <!-- mensagem da escalação: vem LOGO APÓS a caixa — o canal dela define se é
             texto livre (WAHA não oficial) ou template aprovado (WhatsApp oficial). -->
        <div v-if="state.kaTargetInboxId && kaTargetIsFreeText" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Mensagem no WhatsApp
          </label>
          <textarea
            v-model="state.kaMessage"
            rows="3"
            class="w-full px-3 py-2 rounded-xl border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm"
            placeholder="Oi {primeiro_nome}! Vi que tem interesse. Vamos conversar?"
          />
          <p class="text-xs text-n-slate-10">
            WhatsApp não oficial (WAHA) — texto livre. Placeholders:
            <span class="font-mono">{primeiro_nome}</span>,
            <span class="font-mono">{nome}</span>,
            <span class="font-mono">{email}</span>,
            <span class="font-mono">{telefone}</span> e atributos do CRM.
          </p>
        </div>

        <div v-if="state.kaTargetInboxId && !kaTargetIsFreeText" class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Template do WhatsApp (oficial)
          </label>
          <ComboBox
            v-model="state.kaTemplateId"
            :options="kaTemplateOptions"
            placeholder="Selecionar template aprovado"
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <p class="text-xs text-n-slate-10">
            WhatsApp oficial — como o lead não tem janela aberta nessa caixa, exige
            um template aprovado.
          </p>
          <WhatsAppTemplateParser
            v-if="kaSelectedTemplate"
            ref="kaTemplateParserRef"
            :template="kaSelectedTemplate"
          />
        </div>

        <!-- palavras-chave: padrão (sempre ligadas) + extras -->
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            Palavras de ativação
          </label>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="kw in DEFAULT_ACTIVATION_KEYWORDS"
              :key="kw"
              class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-3 text-n-slate-11"
            >
              {{ kw }}
            </span>
          </div>
          <Input
            v-model="state.kaCustomKeywords"
            class="mt-1"
            placeholder="Adicionar outras (separe por vírgula): INFO, AGORA, BORA"
          />
          <p class="text-xs text-n-slate-10">
            As palavras acima já vêm ativadas. A detecção pega várias vertentes:
            ignora acentos e maiúsculas, casa a palavra dentro da frase ("quero
            sim") e aceita letras esticadas ("simmm", "okk", "siim").
          </p>
        </div>

        <!-- Recriar conversa com outro atendente (rodízio na escalação) -->
        <div class="flex flex-col gap-1 pt-1 border-t border-n-weak">
          <label class="flex items-start gap-2 text-sm text-n-slate-12">
            <input v-model="state.kaResetReassign" type="checkbox" class="mt-0.5" />
            <span>
              Recriar a conversa com outro atendente
              <span class="block text-xs text-n-slate-10 font-normal">
                Se o lead já tiver uma conversa aberta nessa caixa, ela é encerrada e
                uma nova é criada e atribuída a outro vendedor (pela atribuição da
                campanha, evitando o atendente anterior) — pra dar a vez a quem ainda
                não falou com ele. O contato é o mesmo (não duplica).
              </span>
            </span>
          </label>
        </div>
      </template>
    </div>

    <!-- Lista de descadastro (LGPD) — global por conta -->
    <div class="flex flex-col gap-2 p-3 rounded-xl bg-n-alpha-2 border border-n-weak">
      <label class="text-sm font-medium text-n-slate-12">
        Lista de descadastro (LGPD)
      </label>
      <p class="text-xs text-n-slate-10 -mt-1">
        Suba um CSV com telefones que NUNCA devem receber campanhas. Vale para
        todas as campanhas desta conta (1ª coluna = telefone).
      </p>
      <label
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak self-start"
      >
        Subir lista de descadastro
        <input
          type="file"
          accept=".csv,.tsv,.txt"
          class="hidden"
          @change="handleSuppressionUpload"
        />
      </label>
      <p v-if="suppressionResult" class="text-xs text-n-teal-11">
        {{ suppressionResult.suppressed }} número(s) adicionado(s) à lista de
        descadastro.
      </p>
    </div>
      </div>
    </Accordion>

    <!-- No fluxo drip o agendamento é OPCIONAL: com data, o disparo inicia sozinho
         na hora marcada (DripSchedulerJob); em branco, inicia no clique em Iniciar. -->
    <div class="flex flex-col gap-1">
      <Input
        v-model="state.scheduledAt"
        :label="
          isDripFlow
            ? 'Iniciar automaticamente em (opcional)'
            : t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')
        "
        type="datetime-local"
        :min="currentDateTime"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
        :message="formErrors.scheduledAt"
        :message-type="formErrors.scheduledAt ? 'error' : 'info'"
      />
      <p v-if="isDripFlow" class="text-xs text-n-slate-10">
        Em branco: a campanha inicia quando você clicar em Iniciar.
      </p>
    </div>

    <!-- Pendência que trava o Criar (espelho do isSubmitDisabled) -->
    <p
      v-if="isSubmitDisabled && missingRequirement"
      class="text-xs text-n-amber-11 text-end -mb-2"
    >
      {{ missingRequirement }}
    </p>
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="button"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
